import checkIfOutputIsVisible from "./checkIfOutputIsVisible";
import evaluateOutputCalculation from "./evaluateOutputCalculation";
import generateExpressionCondition from "./generateExpressionCondition";
import getFlattenedResponses from "./getFlattenedResponses";
import { getOperator, buildAst } from "./dependenciesAst";
import evaluateJsonOutputValue, {retrieveObjectPathValue} from "@utils/evaluateJsonOutputValue";
import getExpressionConfiguration from "./getExpressionConfiguration";
import {getActive} from "./getActive";

function isDisabledInput(inputId, context) {
  const { configuration: blocks } = context;
  const target = blocks?.find((b) => b.id === inputId);
  if (!target) {
    return true
  }
  // if the target block is not found or is not active, it's disabled
  if (!getActive(target, context))
    return true;

  if (!!target.recursive_question_id) {
    // if the target block is inside a recursive question, check if the recursive question is active
    const recursiveQuestion = blocks.find((b) => b.id === target.recursive_question_id);
    if (!recursiveQuestion) {
      return true;
    }
    if (!getActive(recursiveQuestion, context))
      return true;
    // if the recursive question is active, check if its step is active
    const step = blocks.find((b) => b.id === recursiveQuestion.step_id);
    if (!step) {
      return true
    }
    return !getActive(step, context);
  }
  else {
    // if the target block is not inside a recursive question, just check if its step is active
    const step = blocks.find((b) => b.id === target.step_id);
    if (!step) {
      return true
    }
    return !getActive(step, context);
  }
}

function isDisabledOption(optionId, context) {
  const { configuration: blocks } = context;
  const target = blocks?.find((b) => b.id === optionId);
  if (!target) {
    return true;
  }
  return !getActive(target, context);
}

const evaluateBlockDependencies = (
  dependencies,
  context,
  evaluateParent = false,
  recursiveResponsesIndex
) => {
  const { responses: unflattened, configuration } = context;
  const responses = getFlattenedResponses(unflattened);

  // parse the dependency expression to an AST (Abstract Syntax Tree). this is used later to find the parent logic
  // operator ("and" or "or") of a given inputId, in order to determine how to neutralize it if disabled
  const ast = buildAst(dependencies);

  const outputs = configuration.filter((i) => i.type === "output"); // in case of recursive, outputs don't contain all items
  const condition = dependencies
    .map((v, i) => {
      if (v[0] === "{") {
        const { operator, inputId, value, blockType, field } = JSON.parse(v);

        if (isDisabledInput(inputId, context)) {
          // check the parent operator containing the given inputId (in left or right side)
          // if the operator is 'or' return false, if 'and' return true
          // to neutralize the condition depending on disabled input
          const exprOperator = getOperator(inputId, i, ast);
          return exprOperator === "or" ? "false" : "true";
        }
        /*
          If the block is a question with select or multi_select input type and the operator is '===',
          if the selected option is disabled, the condition should be neutralized!
         */
        if (blockType === "question" &&
          ["select", "multi_select"].includes(context?.configuration?.find(b => b.id === inputId)?.input_type) &&
          (operator === "===" || operator === "!==") &&
          isDisabledOption(parseInt(value), context)) {
          const exprOperator = getOperator(inputId, i, ast);
          return exprOperator === "or" ? "false" : "true";
        }
        if (blockType === "output") {
          const output = outputs.find((o) => o.id === inputId);
          const step = configuration.find((c) => c.id === output?.step_id);
          const dependencies = step ? getExpressionConfiguration(step, context, 'dependencies') : [];
          if (
            !output
            || !checkIfOutputIsVisible(output, context)
            || (evaluateParent ? !evaluateBlockDependencies(dependencies, context) : false)  // passthrough
          )
            return "false";

          let outputValue;
          if (output.output_type === "calculation") {
            if (!output.calculation)
              return "false";

            // Exception behavior for remote computation output
            const isSingle = output.calculation.length === 1 && output.calculation[0].startsWith("{");
            const original = isSingle ? JSON.parse(output.calculation[0]).original : undefined;
            if (original === "connector") {
              const parsed = JSON.parse(output.calculation[0]);
              const type = parsed.connector_type || "diaphragm";
              const connectorResults = context.connectionResults?.[type];
              outputValue = retrieveObjectPathValue(connectorResults, field);
            }
            else
              outputValue = evaluateOutputCalculation(output, context, field, recursiveResponsesIndex);
          }

          if (output.output_type === "json_output_value") {
            try {
              outputValue = evaluateJsonOutputValue(output, context);
            }
            catch (error) {
              console.error(
                `JSON part extraction failed for output ${output.id} - will be silently evaluated as False. ` +
                `Check the error below for more information about the issue`
              );
              console.error(error);
              return "false";
            }
          }

          return generateExpressionCondition(
            output,
            outputValue,
            operator,
            value
          );
        }

        const question = responses[inputId]?.question;

        let responseValue =
          !responses[inputId]?.hidden &&
          responses[inputId] &&
          typeof responses[inputId].value !== "undefined"
            ? responses[inputId].value
            : undefined;

        // custom behavior: connector input (custom nested component)
        if (question?.input_type === "connector_input") {
          responseValue = !!field ? responseValue?.[field] : undefined;
        }

        if (question?.input_type === "k_color_input") {
          responseValue = !!field ? responseValue?.[field] : undefined;
          if (field === "kColorProduct")
            responseValue = responseValue?.id
        }

        return generateExpressionCondition(
          question,
          responseValue,
          operator,
          value
        );
      }
      return v;
    })
    .join(" ");

  if (!condition) {
    return false;
  }

  try {
    // console.log(condition);
    // eslint-disable-next-line
    return eval(condition);
  } catch (error) {
    console.log(error);
    return false;
  }
};

export default evaluateBlockDependencies;
